import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Outlet} from "react-router";
import {CurrentAuthorization} from "./interfaces/CurrentAuthorization";
import {fetchAuthorizations} from "./store/auth/authorization.store";
import {AppDispatch, RootState} from "./store/store";
import {isProduction, REACT_APP_USE_KEYCLOAK_IN_DEV} from "./config";
import {useAuth} from "react-oidc-context";
import Loader from "./service/Loader";
import PrivacyApprovalView from "./app/privacy/PrivacyApprovalView";
import {useTranslation} from "react-i18next";
import {signinRedirectUri} from "./service/oidcCallbackUrl";

type PrivateRoutesProps = {
  authField?: keyof CurrentAuthorization;
};
const getIsPageAuthorized = (
  authorization: CurrentAuthorization | null,
  authField?: keyof CurrentAuthorization,
): boolean | null => {
  if (authorization === null) return null;
  if (Object.hasOwn(authorization, "status")) return false;
  if (authField) {
    return authorization[authField] ?? false;
  }
  return true;
};
const PrivateRoutes = ({authField}: PrivateRoutesProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const authorization = useSelector((state: RootState) => state.authorization.data);
  const isPrivacyApprovalRequired = useSelector((state: RootState) => state.privacy.approvalRequired);
  const [isPageAuthorized, setIsPageAuthorized] = useState<boolean | null>(
    getIsPageAuthorized(authorization, authField),
  );
  const auth = useAuth();
  const {t} = useTranslation();

  const isLoggedIn = isProduction || REACT_APP_USE_KEYCLOAK_IN_DEV === "true" ? auth.isAuthenticated : true;

  // Redirection vers la page de login si non authentifié, avec retour sur la page affichée
  useEffect(() => {
    if (!auth.isLoading && !isLoggedIn) {
      // La redirection quitte la page : react-oidc-context porte l'éventuel échec dans `auth.error`.
      void auth.signinRedirect({redirect_uri: signinRedirectUri(window.location.href)});
    }
  }, [auth.isLoading, isLoggedIn, auth]);

  useEffect(() => {
    if (isLoggedIn && authorization === null) {
      void dispatch(fetchAuthorizations());
    }
    setIsPageAuthorized(getIsPageAuthorized(authorization, authField));
  }, [authField, authorization, dispatch, isLoggedIn]);

  if (isPrivacyApprovalRequired) {
    return <PrivacyApprovalView />;
  }

  if (isLoggedIn && isPageAuthorized) {
    return <Outlet />;
  } else if (false === isPageAuthorized) {
    return <>{t("accessDenied")}</>;
  } else {
    return <Loader isOpen={true} />;
  }
};

export default PrivateRoutes;
